import * as React from "react";
import { cn } from "@/lib/utils";

type AvatarImageStatus = "idle" | "loading" | "loaded" | "error";

interface AvatarContextValue {
  imageStatus: AvatarImageStatus;
  setImageStatus: React.Dispatch<React.SetStateAction<AvatarImageStatus>>;
}

const AvatarContext = React.createContext<AvatarContextValue | null>(null);

const useAvatarContext = () => {
  const context = React.useContext(AvatarContext);
  if (!context) {
    throw new Error("Avatar components must be used within Avatar");
  }
  return context;
};

const Avatar = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const [imageStatus, setImageStatus] = React.useState<AvatarImageStatus>("idle");

  return (
    <AvatarContext.Provider value={{ imageStatus, setImageStatus }}>
      <div
        ref={ref}
        className={cn(
          "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
          className
        )}
        {...props}
      />
    </AvatarContext.Provider>
  );
});
Avatar.displayName = "Avatar";

const AvatarImage = React.forwardRef<
  HTMLImageElement,
  React.ImgHTMLAttributes<HTMLImageElement>
>(({ className, src, onLoad, onError, alt = "", ...props }, ref) => {
  const { imageStatus, setImageStatus } = useAvatarContext();
  const imageRef = React.useRef<HTMLImageElement | null>(null);

  const setRefs = React.useCallback(
    (node: HTMLImageElement | null) => {
      imageRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        (ref as React.MutableRefObject<HTMLImageElement | null>).current = node;
      }
    },
    [ref],
  );

  React.useEffect(() => {
    const img = imageRef.current;
    if (!img) {
      setImageStatus("idle");
      return;
    }

    if (img.complete && img.naturalWidth > 0) {
      setImageStatus("loaded");
      return;
    }

    if (img.complete && img.naturalWidth === 0) {
      setImageStatus("error");
      return;
    }

    setImageStatus("idle");
  }, [src, setImageStatus]);

  return (
    <img
      ref={setRefs}
      src={src}
      alt={alt}
      className={cn(
        "aspect-square h-full w-full object-cover",
        imageStatus === "error" && "hidden",
        className
      )}
      onLoad={(event) => {
        setImageStatus("loaded");
        onLoad?.(event);
      }}
      onError={(event) => {
        setImageStatus("error");
        onError?.(event);
      }}
      {...props}
    />
  );
});
AvatarImage.displayName = "AvatarImage";

const AvatarFallback = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => {
  const { imageStatus } = useAvatarContext();

  return (
    <span
      ref={ref}
      aria-hidden={imageStatus === "loaded" ? true : undefined}
      className={cn(
        "absolute inset-0 flex h-full w-full items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground",
        imageStatus === "loaded" && "hidden",
        className
      )}
      {...props}
    />
  );
});
AvatarFallback.displayName = "AvatarFallback";

export { Avatar, AvatarImage, AvatarFallback };
